import {
  PostHogAppContext,
  PostHogEventName as PostHogCustomEvent,
} from "gql/generated";
import { usePostHog as usePostHogHook } from "posthog-js/react";

// Export the enums for use in other files
// These two enums are defined in the back-end and come via the `gql/generated.ts` file
export { PostHogAppContext, PostHogCustomEvent };

export type PostHogCustomProperty =
  | "appContext"
  | "organizationId"
  | "operatorEntityId"
  | "widgetId"
  | "eventId"
  | "bookNowLink"
  | "selectedOperatorId"
  | "selectedFilterTagId"
  | "bookerId"
  | "productId"
  | "promoCode"
  | "orderId"
  | "instanceId";

/**
 * Use this hook to track PostHog events with type safety for event type
 * Must be used within a PostHogProvider context
 * @returns A function to track PostHog events with type safety for event type
 */
export const usePostHog = () => {
  const posthog = usePostHogHook();

  const initPostHog = (name: string, persistence_name?: string) => {
    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      name,
      persistence_name: persistence_name ?? name,
    });
  };

  /**
   * Register a PostHog property with type safety for property type
   * @param posthog - The PostHog instance
   * @param properties - The properties to register
   */
  const registerPostHog = (
    properties: Partial<Record<PostHogCustomProperty, string | number>>,
  ) => {
    posthog.register(properties);
  };

  /**
   * Track a PostHog event with type safety for event type
   * @param event - The event to track
   * @param properties - The properties to track with the event
   */
  const trackPostHogEvent = (
    event: PostHogCustomEvent,
    properties?: Partial<Record<PostHogCustomProperty, string | number>>,
  ) => {
    posthog.capture(event, properties);
  };

  return { posthog, initPostHog, registerPostHog, trackPostHogEvent };
};
